import type { PrimaryFilterId } from '../../../filters/types/filters';
import type { DataProviderSet } from '../../DataProvider/types';
import type { SecondaryDimensionValues } from '../../PageConfigProvider/types/dimension';
import type { MetricValues } from '../../PageConfigProvider/types/metric';
import {
  convertMultiMetricSeriesToTableData,
  convertSeriesToTableData,
  type TableConversionResult,
} from './utils/convertSeriesToTableData';

export const convertTableData = ({
  data,
  secondaryDimension,
  primaryDimension,
}: {
  secondaryDimension?: SecondaryDimensionValues;
  primaryDimension?: PrimaryFilterId;
  data?: Partial<Record<MetricValues, DataProviderSet | undefined>>;
}): TableConversionResult => {
  if (data && Object.keys(data).length > 0) {
    const allKeys = Object.keys(data) as MetricValues[];
    // If any metric has TABLE_ROWS data, filter to only TABLE_ROWS entries
    // to exclude text-only computed values (e.g. cl_market_share).
    const hasTableRows = allKeys.some((k) => data[k]?.type === 'TABLE_ROWS');
    const metricKeys = hasTableRows
      ? allKeys.filter((k) => data[k]?.type === 'TABLE_ROWS')
      : allKeys;

    // If only one metric is selected, use the original convertSeriesToTableData
    // to preserve segmentation functionality
    if (metricKeys.length === 1) {
      const singleMetric = metricKeys?.[0] as MetricValues;
      const singleSeries = data[singleMetric];

      if (singleSeries) {
        const convertedData = convertSeriesToTableData({
          series: singleSeries,
          metric: singleMetric as MetricValues,
          secondaryDimension,
          primaryDimension,
        });
        return convertedData;
      }
    }

    // Multiple metrics - use multi-metric conversion
    const filteredData: Partial<
      Record<MetricValues, DataProviderSet | undefined>
    > = {};
    for (const k of metricKeys) {
      filteredData[k] = data[k];
    }
    const convertedMultiData = convertMultiMetricSeriesToTableData({
      seriesByMetric: filteredData,
      secondaryDimension,
      primaryDimension,
    });

    return convertedMultiData;
  }
  return { rows: [], columnMetaMap: {} };
};
